// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Privacy & data  (route /account/privacy — "Privacy & data", from the profile page)
// Your choices and your rights under the Privacy Policy (§5 consent, §14 rights):
//  · Preferences — order updates / seasonal announcements (utils/consent, saved on THIS device) and analytics cookies (the cookie
//    consent store). They record a choice; ZenFresh sets no analytics cookies today (data/cookies.ts).
//  · Download my data — a JSON file built in the browser from the account's APIs (utils/accountExport). Immediate.
//  · Erase my data — records a request (utils/dataRequests → backend `dataRequests`) that a PERSON reviews; nothing is deleted
//    automatically, and order/payment records may have to be kept for tax and consumer-protection law (Privacy §8).
//  · The grievance contact (details on /policies).
// Deliberately NOT here (no data/system behind it): "DPDP & GDPR compliant" badge, "audited vault v4.2", "zero third-party
// monetisation", GPS/temperature-sensor tracking toggles, recipe/diet personalisation, a 5:30 AM catch broadcast, an encrypted ZIP
// emailed within 2 hours, "Art. 20 compliant", a security OTP for erasure, "FreshCash", 256-bit TLS wording, a Kochi HQ / named DPO.
// Neighbours: pages/Account (row), pages/Policies, utils/consent, hooks/useCookieConsent, pages/Wallet.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Check, CircleAlert, CircleCheck, Clock, Cookie, Download, FileDown, Loader2, LockKeyhole, Trash2, UserRound } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { CONTACT_EMAIL, GRIEVANCE_OFFICER_NAME } from "../data/legalContact";
import useAuth from "../hooks/useAuth";
import useCookieConsent from "../hooks/useCookieConsent";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { downloadMyData } from "../utils/accountExport";
import { ApiError } from "../utils/api";
import { readConsent, saveConsent } from "../utils/consent";
import { MAX_NOTE, fetchErasure, requestErasure, type ErasureRequest } from "../utils/dataRequests";
import { fetchOrders, formatWhen } from "../utils/orders";
import { formatInr } from "../utils/priceFormat";
import { fetchWallet } from "../utils/wallet";

const card = "rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";

interface SwitchRowProps {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

function SwitchRow({ id, title, description, checked, onChange }: SwitchRowProps) {
  return (
    <li className="flex items-start justify-between gap-3 py-3">
      <div className="min-w-0">
        <p id={`${id}-l`} className="text-sm font-bold text-ocean-950 dark:text-foam-50">{title}</p>
        <p id={`${id}-d`} className="mt-0.5 text-xs text-ocean-800/85 dark:text-foam-100/80">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-l`}
        aria-describedby={`${id}-d`}
        onClick={() => onChange(!checked)}
        className={`relative mt-1 h-7 w-12 shrink-0 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 motion-reduce:transition-none ${checked ? "bg-ocean-800" : "bg-ocean-700/25 dark:bg-white/25"}`}
      >
        <span aria-hidden="true" className={`absolute left-0.5 top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-ocean-800 shadow transition-transform motion-reduce:transition-none ${checked ? "translate-x-5" : ""}`}>
          {checked && <Check className="h-3.5 w-3.5" />}
        </span>
      </button>
    </li>
  );
}

export default function PrivacyData() {
  useDocumentTitle("Privacy & data");
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const cookies = useCookieConsent();
  const [consent, setConsent] = useState(() => readConsent());
  const [erasure, setErasure] = useState<ErasureRequest | null | undefined>(undefined); // undefined = loading
  const [balance, setBalance] = useState<number | null>(null);
  const [activeOrders, setActiveOrders] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    fetchErasure().then((r) => !cancelled && setErasure(r.request), () => !cancelled && setErasure(null));
    fetchWallet().then((w) => !cancelled && setBalance(w.balance), () => undefined);
    fetchOrders().then((o) => !cancelled && setActiveOrders(o.filter((order) => order.active).length), () => undefined);
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  useEffect(() => {
    if (confirming) dialog.current?.focus();
  }, [confirming]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const setMessage = (key: "orderUpdates" | "seasonalDrops", value: boolean) =>
    setConsent(saveConsent({ orderUpdates: key === "orderUpdates" ? value : (consent?.orderUpdates ?? false), seasonalDrops: key === "seasonalDrops" ? value : (consent?.seasonalDrops ?? false) }));

  const download = async () => {
    setExporting(true);
    setError(null);
    try {
      await downloadMyData();
      setExported(true);
    } catch {
      setError("We couldn't prepare your data. Check your connection and try again.");
    } finally {
      setExporting(false);
    }
  };

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      setErasure(await requestErasure(note));
      setConfirming(false);
      setNote("");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't send that. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const canAsk = erasure === null || erasure?.status === "declined";

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-5 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Privacy &amp; data</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Your choices and your rights</p>
            </div>
          </header>

          <section className="rounded-2xl bg-ocean-900 p-4 text-foam-50 shadow-md">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold"><LockKeyhole className="h-5 w-5 text-teal-300" aria-hidden="true" /> Your data, your say</h2>
            <p className="mt-1 text-sm text-foam-100/90">Under our Privacy Policy (§14) you can see, correct and ask us to erase your personal data, and change what optional things you allow (§5).</p>
            <Link to="/privacy" className="mt-3 inline-flex min-h-11 items-center rounded-full bg-white px-4 text-sm font-bold text-ocean-900 hover:bg-foam-100">Read the Privacy Policy</Link>
          </section>

          <Link to="/coming-soon/privacy-vault" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>Advanced privacy controls</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          <p role="alert" className="flex min-h-5 items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300">
            {error && <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />}
            {error}
          </p>

          <section aria-labelledby="prefs-h" className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h2 id="prefs-h" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Preferences</h2>
              <span className="text-xs text-ocean-800/80 dark:text-foam-100/75">Saved on this device</span>
            </div>
            <ul className={`${card} divide-y divide-ocean-900/10 dark:divide-white/10`}>
              <SwitchRow id="order-updates" title="Order updates on WhatsApp" description="Messages about your order's progress, if ZenFresh messages you on WhatsApp." checked={consent?.orderUpdates ?? false} onChange={(v) => setMessage("orderUpdates", v)} />
              <SwitchRow id="seasonal" title="Seasonal catch announcements" description="Occasional news when a seasonal catch arrives." checked={consent?.seasonalDrops ?? false} onChange={(v) => setMessage("seasonalDrops", v)} />
              <SwitchRow
                id="analytics"
                title="Analytics cookies"
                description="ZenFresh sets no analytics cookies today; this records your choice for if that changes."
                checked={cookies.preferences?.analytics ?? false}
                onChange={(v) => cookies.savePreferences({ essential: true, marketing: cookies.preferences?.marketing ?? false, analytics: v })}
              />
            </ul>
            <button type="button" onClick={cookies.openPreferences} className="inline-flex min-h-11 items-center gap-2 self-start rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">
              <Cookie className="h-4 w-4" aria-hidden="true" /> All cookie preferences
            </button>
          </section>

          <section aria-labelledby="rights-h" className="flex flex-col gap-2">
            <h2 id="rights-h" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Your data</h2>
            <div className={`${card} flex flex-col gap-3`}>
              <div className="flex items-start gap-3">
                <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ocean-700/10 text-ocean-800 dark:bg-white/10 dark:text-foam-100"><FileDown className="h-6 w-6" /></span>
                <div>
                  <h3 className="text-sm font-bold text-ocean-950 dark:text-foam-50">Download my data</h3>
                  <p className="mt-0.5 text-xs text-ocean-800/85 dark:text-foam-100/80">One file with your profile, addresses, orders (with invoice numbers), wallet history, favourites and complaints. It downloads straight away — nothing is emailed.</p>
                </div>
              </div>
              <button type="button" onClick={() => void download()} disabled={exporting} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-ocean-800 px-6 font-bold text-white shadow-md hover:bg-ocean-700 disabled:opacity-60">
                {exporting ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Download className="h-5 w-5" aria-hidden="true" />} Download my data (.json)
              </button>
              <p role="status" className="min-h-4 text-xs font-semibold text-teal-700 dark:text-teal-300">{exported ? "Your file has been downloaded." : ""}</p>
              <div className="flex items-start gap-3 border-t border-ocean-900/10 pt-3 dark:border-white/10">
                <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ocean-700/10 text-ocean-800 dark:bg-white/10 dark:text-foam-100"><UserRound className="h-6 w-6" /></span>
                <div>
                  <h3 className="text-sm font-bold text-ocean-950 dark:text-foam-50">Correct my details</h3>
                  <p className="mt-0.5 text-xs text-ocean-800/85 dark:text-foam-100/80">Update your <Link to="/profile/edit" className="font-bold underline">name, nickname and email</Link> or your <Link to="/account/addresses" className="font-bold underline">delivery addresses</Link> yourself, any time.</p>
                </div>
              </div>
            </div>
          </section>

          <section aria-labelledby="erase-h" className="flex flex-col gap-2">
            <h2 id="erase-h" className="font-display text-lg font-bold text-coral-600 dark:text-coral-300">Erase my data</h2>
            <div className={`${card} flex flex-col gap-3`}>
              <div className="flex items-start gap-3">
                <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-coral-500/10 text-coral-600 dark:text-coral-300"><Trash2 className="h-6 w-6" /></span>
                <div>
                  <h3 className="text-sm font-bold text-ocean-950 dark:text-foam-50">Ask us to delete or anonymise my personal data</h3>
                  <p className="mt-0.5 text-xs text-ocean-800/85 dark:text-foam-100/80">A person at ZenFresh reviews your request. Order and payment records may have to be kept for the period tax and consumer-protection law require (Privacy Policy §8); everything else we&apos;ll delete or anonymise.</p>
                </div>
              </div>

              {(balance !== null || activeOrders !== null) && (
                <p className="rounded-xl bg-ocean-700/5 p-3 text-xs text-ocean-800/90 dark:bg-white/5 dark:text-foam-100/85">
                  Worth knowing first: {balance !== null && <>you have <strong>{formatInr(balance)}</strong> in your wallet</>}
                  {balance !== null && activeOrders !== null && " and "}
                  {activeOrders !== null && <><strong>{activeOrders}</strong> active {activeOrders === 1 ? "order" : "orders"}</>}. Tell us in the note how you&apos;d like them handled.
                </p>
              )}

              {erasure === undefined && <p role="status" className="flex items-center gap-2 text-sm text-ocean-800 dark:text-foam-100"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Checking…</p>}

              {erasure && erasure.status === "open" && (
                <p role="status" className="flex items-start gap-2 rounded-xl bg-ocean-700/10 p-3 text-sm font-semibold text-ocean-900 dark:bg-white/10 dark:text-foam-50">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> Request received {formatWhen(erasure.createdAt)}. A person will review it and get in touch.
                </p>
              )}
              {erasure && erasure.status === "completed" && (
                <p role="status" className="flex items-start gap-2 rounded-xl bg-teal-100 p-3 text-sm font-semibold text-teal-700">
                  <CircleCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> Completed {erasure.resolvedAt ? formatWhen(erasure.resolvedAt) : ""}.{erasure.resolutionNote ? ` ${erasure.resolutionNote}` : ""}
                </p>
              )}
              {erasure && erasure.status === "declined" && (
                <p role="status" className="rounded-xl bg-coral-500/10 p-3 text-sm text-ocean-950 dark:text-foam-50">
                  We couldn&apos;t complete your last request{erasure.resolutionNote ? `: ${erasure.resolutionNote}` : "."} You can ask again.
                </p>
              )}

              {canAsk && !confirming && (
                <button type="button" onClick={() => { setError(null); setConfirming(true); }} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-coral-500/10 px-6 font-bold text-coral-600 hover:bg-coral-500/20 dark:text-coral-300">
                  <Trash2 className="h-5 w-5" aria-hidden="true" /> Request erasure of my data
                </button>
              )}

              {canAsk && confirming && (
                <div ref={dialog} tabIndex={-1} role="alertdialog" aria-labelledby="erase-confirm-h" aria-describedby="erase-confirm-d" className="rounded-xl bg-coral-500/10 p-3 outline-none">
                  <h3 id="erase-confirm-h" className="text-sm font-bold text-ocean-950 dark:text-foam-50">Send this request?</h3>
                  <p id="erase-confirm-d" className="mt-1 text-xs text-ocean-800/90 dark:text-foam-100/85">We&apos;ll review it and remove or anonymise your personal data, apart from records we&apos;re required to keep. You may lose access to your account and its history.</p>
                  <label htmlFor="erase-note" className="mt-2 block text-xs font-bold text-ocean-900 dark:text-foam-50">Anything we should know? (optional)</label>
                  <textarea id="erase-note" value={note} maxLength={MAX_NOTE} onChange={(event) => setNote(event.target.value)} rows={3} className="mt-1 w-full rounded-xl bg-white p-2.5 text-sm text-ocean-950 ring-1 ring-ocean-900/15 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-ocean-900 dark:text-foam-50" />
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={() => void send()} disabled={sending} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-coral-600 px-4 text-sm font-bold text-white disabled:opacity-60">
                      {sending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Yes, send request
                    </button>
                    <button type="button" onClick={() => setConfirming(false)} className="min-h-11 rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="flex flex-col gap-2 rounded-2xl bg-ocean-700/5 p-4 dark:bg-white/5">
            <h2 className="text-sm font-bold text-ocean-950 dark:text-foam-50">Questions or a complaint about your data?</h2>
            <p className="text-xs text-ocean-800/90 dark:text-foam-100/85">Write to our Grievance Officer ({GRIEVANCE_OFFICER_NAME}) at <a href={`mailto:${CONTACT_EMAIL}`} className="break-all font-bold underline">{CONTACT_EMAIL}</a>. We acknowledge within 48 hours (Privacy §15). <Link to="/policies" className="font-bold underline">Grievance details</Link></p>
          </section>
        </div>
      </div>
    </PageTransition>
  );
}
